import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const ThemeContext = createContext(null);

// 'light' | 'dark' | 'system'
const STORAGE_KEY = "ctp_theme";

function applyTheme(mode) {
  // mode is the resolved theme: 'light' or 'dark'
  const root = document.documentElement;
  if (mode === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

function resolvePreference(pref) {
  if (pref === "dark" || pref === "light") return pref;
  // Default: dark for everyone, on every device, unless explicitly switched.
  // We still honour `system` if the user picks it manually.
  if (pref === "system" && typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return "dark";
}

export function ThemeProvider({ children }) {
  const [pref, setPref] = useState(() => {
    if (typeof window === "undefined") return "dark";
    // First visit (no stored pref) → default to dark across all devices.
    return localStorage.getItem(STORAGE_KEY) || "dark";
  });
  const [resolved, setResolved] = useState(() => resolvePreference(pref));

  // Apply on mount + when pref changes
  useEffect(() => {
    const next = resolvePreference(pref);
    setResolved(next);
    applyTheme(next);
    // Persist explicit picks ("light", "dark", or "system"). Removing the key
    // would silently flip new sessions to dark — which IS the new default,
    // but we still want to remember an explicit "system" choice.
    localStorage.setItem(STORAGE_KEY, pref);
  }, [pref]);

  // Listen to OS theme changes when in "system" mode
  useEffect(() => {
    if (pref !== "system" || typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => {
      const next = e.matches ? "dark" : "light";
      setResolved(next);
      applyTheme(next);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [pref]);

  const setTheme = useCallback((next) => setPref(next), []);
  const toggle = useCallback(() => {
    setPref((current) => {
      const r = resolvePreference(current);
      return r === "dark" ? "light" : "dark";
    });
  }, []);

  const value = useMemo(
    () => ({ pref, resolved, setTheme, toggle }),
    [pref, resolved, setTheme, toggle]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
